import contains from 'dom-helpers/query/contains';
import ownerDocument from 'dom-helpers/ownerDocument';
import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback } from 'react';
import addEventListener from './utils/addEventListener';
const escapeKeyCode = 27;
function isLeftClickEvent(event) {
  return event.button === 0;
}
function isModifiedEvent(event) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

/**
 * The `<RootCloseWrapper/>` component registers your callback on the document
 * when rendered. Powers the `<Overlay/>` component. This is used achieve modal
 * style behavior where your callback is triggered when the user tries to
 * interact with the rest of the document or hits the `esc` key.
 */
function RootCloseWrapper(_ref) {
  let {
    disabled,
    event = 'click',
    onRootClose,
    children
  } = _ref;
  const preventMouseRootCloseRef = useRef(false);
  const currentEventRef = useRef(null);
  const childRef = useRef(null);
  const handleMouseCapture = useCallback(e => {
    preventMouseRootCloseRef.current = isModifiedEvent(e) || !isLeftClickEvent(e) || childRef.current && contains(childRef.current, e.target);
  }, []);
  const handleMouse = useCallback(e => {
    // Skip if this event is the same as the one running when we added the handlers
    if (e === currentEventRef.current) {
      currentEventRef.current = undefined;
      return;
    }
    if (!preventMouseRootCloseRef.current && onRootClose) {
      onRootClose(e);
    }
  }, [onRootClose]);
  const handleKeyUp = useCallback(e => {
    // Skip if this event is the same as the one running when we added the handlers
    if (e === currentEventRef.current) {
      currentEventRef.current = undefined;
      return;
    }
    if (e.keyCode === escapeKeyCode && onRootClose) {
      onRootClose(e);
    }
  }, [onRootClose]);
  useEffect(() => {
    if (disabled || !onRootClose) {
      return undefined;
    }

    // Store the current event to avoid triggering handlers immediately
    // https://github.com/facebook/react/issues/20074
    currentEventRef.current = window.event;

    // Get the document from the child element, or fall back to global document
    const doc = childRef.current ? ownerDocument(childRef.current) : document;

    // Use capture for this listener so it fires before React's listener, to
    // avoid false positives in the contains() check below if the target DOM
    // element is removed in the React mouse callback.
    const documentMouseCaptureListener = addEventListener(doc, event, handleMouseCapture, true);
    const documentMouseListener = addEventListener(doc, event, handleMouse);
    const documentKeyupListener = addEventListener(doc, 'keyup', handleKeyUp);
    return () => {
      if (documentMouseCaptureListener) {
        documentMouseCaptureListener.remove();
      }
      if (documentMouseListener) {
        documentMouseListener.remove();
      }
      if (documentKeyupListener) {
        documentKeyupListener.remove();
      }
    };
  }, [disabled, event, onRootClose, handleMouseCapture, handleMouse, handleKeyUp]);

  // Clone the child to attach the ref
  return /*#__PURE__*/React.cloneElement(React.Children.only(children), {
    ref: childRef
  });
}
RootCloseWrapper.displayName = 'RootCloseWrapper';
export default RootCloseWrapper;